import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSignIn, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { Loader2, AlertCircle } from 'lucide-react';

export default function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, setActive, isLoaded } = useSignIn();
  const { signOut: clerkSignOut, isSignedIn } = useClerkAuth();

  const prefillEmail = location.state?.prefillEmail || '';

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sign out any existing Clerk session the moment the login screen mounts.
  // Clerk's HttpOnly session cookie can survive localStorage/cookie clears on
  // iPhone Safari. Calling signOut() here, after ClerkProvider has initialised,
  // is the most reliable way to invalidate it before the user submits credentials.
  useEffect(() => {
    clerkSignOut().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prefillEmail) {
      document.getElementById('password')?.focus();
    }
  }, [prefillEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;
    setLoading(true);
    setError(null);
    try {
      // If a Clerk session is still active when the user submits (race with the
      // mount signOut), clear it now and wait for it to complete before creating
      // a new sign-in. Prevents the "already signed in" 400 from Clerk.
      if (isSignedIn) {
        await clerkSignOut();
      }

      let result = await signIn.create({ identifier: email, password });

      if (result.status === 'needs_first_factor') {
        result = await signIn.attemptFirstFactor({ strategy: 'password', password });
      }

      if (result.status === 'complete') {
        // MFA disabled or not required — proceed directly into the app
        await setActive({ session: result.createdSessionId });
      } else if (result.status === 'needs_second_factor') {
        // MFA is enabled and required — go to 2FA screen
        await signIn.prepareSecondFactor({ strategy: 'email_code' });
        navigate('/verify-2fa', { state: { email } });
      } else {
        setError('Sign in could not be completed. Please try again or contact support.');
      }
    } catch (err) {
      const e0 = err.errors?.[0];
      setError(e0?.longMessage || e0?.message || 'Incorrect email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = email && password && !loading;

  return (
    <div style={{
      minHeight: '100vh', background: '#001a4d',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px', fontFamily: "'Inter', sans-serif",
    }} data-testid="login-screen">

      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        {/* Plate-style logo */}
        <div style={{
          background: '#F5C400', border: '3px solid #111',
          borderRadius: '8px', display: 'inline-block',
          padding: '10px 28px', marginBottom: '16px',
        }}>
          <div style={{
            fontFamily: "'Oswald', sans-serif", fontSize: '36px',
            fontWeight: 700, color: '#111', letterSpacing: '8px',
          }}>VCIN</div>
        </div>
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '11px',
          fontWeight: 600, letterSpacing: '3px', color: 'rgba(255,255,255,0.5)',
          textTransform: 'uppercase',
        }}>Vehicle Crime Intelligence Network</div>
      </div>

      {/* Card */}
      <div style={{
        background: 'white', borderRadius: '16px', padding: '28px 24px',
        width: '100%', maxWidth: '380px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>

        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '18px',
          fontWeight: 700, color: '#001a4d', letterSpacing: '2px',
          textTransform: 'uppercase', marginBottom: '20px',
        }}>Sign In</div>

        {error && (
          <div style={{
            background: '#fff5f5', border: '1.5px solid #fecaca',
            borderLeft: '4px solid #dc2626', borderRadius: '10px',
            padding: '12px 14px', marginBottom: '16px',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }} data-testid="login-error">
            <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={{ fontSize: '13px', color: '#dc2626', lineHeight: 1.4 }}>{error}</span>
          </div>
        )}

        {/* Email */}
        <div style={{ marginBottom: '14px' }}>
          <label htmlFor="email" style={{
            display: 'block', fontFamily: "'Oswald', sans-serif",
            fontSize: '10px', fontWeight: 600, letterSpacing: '2px',
            textTransform: 'uppercase', color: '#4a5a7a', marginBottom: '8px',
          }}>Email Address</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="officer@company.co.uk"
            required
            autoComplete="email"
            style={{
              width: '100%', height: '48px', padding: '0 14px',
              background: '#f7f8fc', border: '1.5px solid #e4e8f0',
              borderRadius: '10px', fontSize: '14px', color: '#001a4d',
              fontFamily: "'Inter', sans-serif", outline: 'none',
              boxSizing: 'border-box',
            }}
            data-testid="login-email-input"
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label htmlFor="password" style={{
              fontFamily: "'Oswald', sans-serif", fontSize: '10px',
              fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: '#4a5a7a',
            }}>Password</label>
            <Link to="/forgot-password" style={{
              fontSize: '12px', color: '#001a4d', fontWeight: 600,
              textDecoration: 'none', opacity: 0.6,
            }} data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <input
            id="password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            autoComplete="current-password"
            style={{
              width: '100%', height: '48px', padding: '0 14px',
              background: '#f7f8fc', border: '1.5px solid #e4e8f0',
              borderRadius: '10px', fontSize: '14px', color: '#001a4d',
              fontFamily: "'Inter', sans-serif", outline: 'none',
              boxSizing: 'border-box',
            }}
            data-testid="login-password-input"
          />
        </div>

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            width: '100%', height: '50px', border: 'none', borderRadius: '10px',
            background: canSubmit ? '#001a4d' : '#c8d0e0',
            color: canSubmit ? 'white' : '#8a9ab8',
            fontFamily: "'Oswald', sans-serif", fontSize: '16px',
            fontWeight: 700, letterSpacing: '4px', textTransform: 'uppercase',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            transition: 'all 0.15s',
          }}
          data-testid="login-submit-btn"
        >
          {loading
            ? <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Signing in...</>
            : 'Sign In'
          }
        </button>
      </div>

      <div style={{
        marginTop: '24px', fontSize: '11px',
        color: 'rgba(255,255,255,0.25)', textAlign: 'center', lineHeight: 1.6,
      }}>
        Authorised users only · SIA Licensed Personnel
        <br />
        v{process.env.REACT_APP_BUILD_VERSION || 'dev'}
      </div>
    </div>
  );
}
